import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { pizzaData, sectionImages } from '../data/menuData';
import { Flame, Leaf, Star, Sparkles } from 'lucide-react';

const PIZZA_BG = sectionImages.pizza;

const PizzaSection = () => {
  const { language, t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { key: 'all', label: t('menu.pizza.filters.all') },
    { key: 'vegetarisch', label: t('menu.pizza.filters.vegetarisch') },
    { key: 'vegan', label: t('menu.pizza.filters.vegan') },
    { key: 'chicken', label: t('menu.pizza.filters.chicken') },
    { key: 'meeresfrüchte', label: t('menu.pizza.filters.meeresfrüchte') },
    { key: 'rindfleisch', label: t('menu.pizza.filters.rindfleisch') },
  ];

  const filteredPizzas = activeFilter === 'all'
    ? pizzaData
    : pizzaData.filter(pizza => pizza.category === activeFilter);

  const renderBadges = (pizza) => {
    const badges = [];
    if (pizza.isPopular) badges.push({ icon: <Star className="w-4 h-4 text-amber-400 fill-amber-400" />, key: 'popular' });
    if (pizza.isSpicy) badges.push({ icon: <Flame className="w-4 h-4 text-orange-500" />, key: 'spicy' });
    if (pizza.isVegetarian) badges.push({ icon: <Leaf className="w-4 h-4 text-emerald-500" />, key: 'vegetarian' });
    if (pizza.isVegan) badges.push({ icon: <Leaf className="w-4 h-4 text-emerald-600 fill-emerald-600" />, key: 'vegan' });
    
    if (badges.length === 0) return null;
    return (
      <div className="flex items-center gap-1.5 ml-2">
        {badges.map((b) => <span key={b.key}>{b.icon}</span>)}
      </div>
    );
  };

  const sizeLabels = {
    de: { klein: '26cm', mittel: '32cm', gross: '40cm', party: '60×40' },
    en: { klein: '26cm', mittel: '32cm', gross: '40cm', party: '60×40' }
  };
  const sizes = sizeLabels[language] || sizeLabels.de;

  return (
    <section 
      id="speisekarte" 
      className="relative overflow-hidden"
      data-testid="pizza-section"
    >
      {/* Premium Section Header */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${PIZZA_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/50 via-hanse-dark/60 to-hanse-dark/95" />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 mb-4 bg-gradient-to-r from-amber-500/30 to-amber-400/20 backdrop-blur-sm px-5 py-2.5 rounded-full border-2 border-amber-400/60 shadow-lg shadow-amber-500/20"
          >
            <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
            <span className="text-amber-300 font-bold text-sm md:text-base uppercase tracking-wider">
              {language === 'de' ? 'Handgemacht mit Liebe' : 'Handmade with Love'}
            </span>
            <Sparkles className="w-5 h-5 text-hanse-red" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
          >
            {language === 'de' ? 'Unsere Pizzen' : 'Our Pizzas'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/70 text-base md:text-lg max-w-2xl italic"
          >
            {language === 'de' 
              ? 'Wo Leidenschaft, Geschmack und Gemeinschaft auf den Tisch kommen 🍕✨'
              : 'Where passion, flavor, and togetherness come to the table 🍕✨'
            }
          </motion.p>
        </div>
      </div>

      {/* Premium Menu Content */}
      <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-10 md:py-14">
        <div className="container mx-auto px-4 max-w-6xl">
          
          {/* Filter Tabs - Premium Style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-2 mb-10"
          >
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                  activeFilter === filter.key
                    ? 'bg-gradient-to-r from-hanse-red to-hanse-red/80 text-white shadow-lg shadow-hanse-red/25'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10 hover:border-white/20'
                }`}
                data-testid={`pizza-filter-${filter.key}`}
              >
                {filter.label}
              </button>
            ))}
          </motion.div>

          {/* Size Headers - Premium */}
          <div className="hidden lg:grid grid-cols-[1fr,auto] gap-8 mb-4 px-6">
            <div></div>
            <div className="flex gap-3">
              <span className="w-20 text-center text-white/50 text-sm font-medium">{sizes.klein}</span>
              <span className="w-20 text-center text-white/50 text-sm font-medium">{sizes.mittel}</span>
              <span className="w-20 text-center text-white/50 text-sm font-medium">{sizes.gross}</span>
              <span className="w-24 text-center text-amber-400/70 text-sm font-bold bg-amber-400/10 rounded-full py-1">{sizes.party}</span>
            </div>
          </div>

          {/* Pizza Items - Premium Cards */}
          <div className="space-y-2">
            {filteredPizzas.map((pizza, index) => (
              <motion.div
                key={pizza.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(index * 0.03, 0.2) }}
                className="group relative bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:border-hanse-red/30 hover:bg-white/10 transition-all duration-300"
                data-testid={`pizza-item-${pizza.id}`}
              >
                {/* Popular badge */}
                {pizza.isPopular && (
                  <div className="absolute -top-2 left-4 bg-gradient-to-r from-hanse-red to-hanse-red/80 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    <Star className="w-3 h-3 inline mr-1 fill-white" />
                    {language === 'de' ? 'Beliebt' : 'Popular'}
                  </div>
                )}

                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Pizza Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-1">
                      <h3 className="font-heading text-lg font-bold text-white group-hover:text-hanse-red transition-colors">
                        {language === 'de' ? pizza.name : pizza.nameEn}
                      </h3>
                      {renderBadges(pizza)}
                    </div>
                    <p className="text-white/50 text-sm mt-1 leading-relaxed">
                      {language === 'de' ? pizza.ingredients : pizza.ingredientsEn}
                    </p>
                  </div>

                  {/* Prices */}
                  <div className="flex items-center gap-3 lg:gap-3">
                    {/* Mobile Grid */}
                    <div className="grid grid-cols-4 gap-2 lg:hidden w-full">
                      <div className="flex flex-col items-center bg-white/5 rounded-lg p-2">
                        <span className="text-[10px] text-white/40">{sizes.klein}</span>
                        <span className="font-bold text-white text-sm">€{pizza.prices.klein.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-col items-center bg-white/5 rounded-lg p-2">
                        <span className="text-[10px] text-white/40">{sizes.mittel}</span>
                        <span className="font-bold text-white text-sm">€{pizza.prices.mittel.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-col items-center bg-white/5 rounded-lg p-2">
                        <span className="text-[10px] text-white/40">{sizes.gross}</span>
                        <span className="font-bold text-white text-sm">€{pizza.prices.gross.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-col items-center bg-amber-400/20 rounded-lg p-2 border border-amber-400/30">
                        <span className="text-[10px] text-amber-400/70 font-medium">{sizes.party}</span>
                        <span className="font-bold text-amber-400 text-sm">€{pizza.prices.party.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Desktop Row */}
                    <div className="hidden lg:flex items-center gap-3">
                      <span className="font-bold text-white w-20 text-center">€{pizza.prices.klein.toFixed(2)}</span>
                      <span className="font-bold text-white w-20 text-center">€{pizza.prices.mittel.toFixed(2)}</span>
                      <span className="font-bold text-white w-20 text-center">€{pizza.prices.gross.toFixed(2)}</span>
                      <span className="font-bold text-amber-400 w-24 text-center bg-amber-400/10 rounded-full py-2 border border-amber-400/20">
                        €{pizza.prices.party.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Size Legend - Premium */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-wrap justify-center items-center gap-6 text-sm"
          >
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-white/30"></div>
              <span className="text-white/50">{language === 'de' ? 'Klein' : 'Small'} (26cm)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-white/50"></div>
              <span className="text-white/50">{language === 'de' ? 'Mittel' : 'Medium'} (32cm)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-white/70"></div>
              <span className="text-white/50">{language === 'de' ? 'Groß' : 'Large'} (40cm)</span>
            </div>
            <div className="flex items-center gap-2 bg-amber-400/10 px-3 py-1 rounded-full border border-amber-400/20">
              <div className="w-3 h-3 rounded-full bg-amber-400"></div>
              <span className="text-amber-400 font-medium">Party (60×40) — {language === 'de' ? 'Familiengröße' : 'Family Size'}</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PizzaSection;
